import React, {
  useContext, useRef, useState, useEffect, useCallback
} from 'react';
import PropTypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import {
  Alert,
  Button,
  DrawerBody,
  DrawerFooter,
  Typography,
} from '@one-thd/sui-atomic-components';
import { ProductImage } from '@thd-olt-component-react/product-pod';
import { InlineProjectCreation } from '@thd-olt-component-react/project-forms';
import { AddToCoreContext } from '../../core/AddToCoreContext';
import { TRACK } from '../../AddToConstants';
import { triggerAnalytics } from '../../AddToHelpers';

const INITIAL_SIZE = 60;
const FOLLOWING_SIZE = 70;
const THUMBNAIL_GAP = 8; // sui-gap-2 = 8px
const MAX_THUMBNAILS = 5;

export const CreateProjectPreview = ({
  products,
  singleProduct,
  multipleProduct,
  onProjectCreatedAndItemAdded,
  copyLocation,
}) => {
  const { handleClose } = useContext(AddToCoreContext);
  const containerRef = useRef(null);
  const [maxThumbnails, setMaxThumbnails] = useState(MAX_THUMBNAILS);
  const [successAlert, setSuccessAlert] = useState(null);

  const calculateMaxThumbnails = useCallback(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      // First thumbnail is 60px, rest are 70px, with 8px gaps
      // Also account for the Add icon (~24px + gap) between first and second
      const addIconSpace = 24 + THUMBNAIL_GAP;
      let usedWidth = INITIAL_SIZE + THUMBNAIL_GAP + addIconSpace;
      let count = 1;
      while (count < MAX_THUMBNAILS && usedWidth + FOLLOWING_SIZE <= containerWidth) {
        usedWidth += FOLLOWING_SIZE + THUMBNAIL_GAP;
        count += 1;
      }
      setMaxThumbnails(Math.max(count, 1));
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    calculateMaxThumbnails();
    window.addEventListener('resize', calculateMaxThumbnails);
    return () => window.removeEventListener('resize', calculateMaxThumbnails);
  }, [calculateMaxThumbnails]);

  const visibleProducts = products.slice(0, maxThumbnails);
  const remainingCount = products.length - maxThumbnails;
  const showOverflow = remainingCount > 0;

  const handleCancel = () => {
    handleClose();
  };

  const handleInlineCreationSuccess = ({ projectId, projectName }) => {
    setSuccessAlert({
      itemCount: products.length,
      projectName,
    });

    triggerAnalytics(TRACK.ADD_TO_LIST_ADD_ACTION, {
      name: 'new project project add',
      location: copyLocation || '',
      items: products.map((prod) => ({
        quantity: prod.quantity || 1,
        price: prod.pricing?.value || prod.price,
        sku: prod.itemId,
        aiQuantityEstimate: prod?.aiQuantityEstimate ? 'yes' : 'no'
      })),
      projectId,
      uniqueSkuCount: new Set(products.map((p) => p.itemId)).size,
      projectAddTotal: products.reduce(
        (total, prod) => total + ((prod.pricing?.value || prod.price || 0) * (prod.quantity || 1)), 0
      ),
      projectStarter: 'copy from your lists',
    });

    if (onProjectCreatedAndItemAdded) {
      onProjectCreatedAndItemAdded({ projectId, projectName });
    }
  };

  return (
    <>
      <DrawerBody>
        {/* Success Alert */}
        {successAlert && (
          <div className="sui-mb-9">
            <Alert
              status="success"
              onClose={() => setSuccessAlert(null)}
              data-testid="create-project-success-alert"
            >
              {successAlert.itemCount} {successAlert.itemCount === 1 ? 'item has' : 'items have'} been
              copied from your Lists.
            </Alert>
          </div>
        )}
        {/* Product Thumbnails */}
        <div ref={containerRef} className="sui-flex sui-gap-2 sui-mb-5 sui-items-center sui-justify-between sui-mx-9">
          {visibleProducts.map((product, index) => {
            const isLastSlot = index === maxThumbnails - 1 && showOverflow;

            const size = index === 0 ? { width: 60, height: 60 } : { width: 70, height: 70 };

            if (isLastSlot) {
              return (
                <React.Fragment key={product.itemId}>
                  {index === 1 && (
                    <div className="sui-flex sui-items-center">
                      <span className="sui-text-lg sui-font-bold">+</span>
                    </div>
                  )}
                  <div
                    className="sui-relative sui-flex-shrink-0 sui-overflow-hidden"
                    style={size}
                  >
                    <div className="sui-opacity-30">
                      <ProductImage itemId={product.itemId} />
                    </div>
                    <div
                      className="sui-absolute sui-inset-0 sui-flex sui-items-center sui-justify-center"
                    >
                      <Typography variant="h4" weight="bold">
                        +{remainingCount}
                      </Typography>
                    </div>
                  </div>
                </React.Fragment>
              );
            }

            return (
              <React.Fragment key={product.itemId}>
                {index === 1 && (
                  <div className="sui-flex sui-items-center">
                    <span className="sui-text-lg sui-font-bold">+</span>
                  </div>
                )}
                <div
                  className="sui-flex-shrink-0 sui-overflow-hidden"
                  style={size}
                >
                  <ProductImage itemId={product.itemId} />
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Inline Project Creation */}
        <InlineProjectCreation
          products={products}
          singleProduct={singleProduct}
          multipleProduct={multipleProduct}
          onSuccess={handleInlineCreationSuccess}
        />
      </DrawerBody>

      <DrawerFooter style={{ boxShadow: '0 1px 8px 0 rgb(0 0 0 / .375)' }}>
        <Button
          variant="secondary"
          fullWidth
          onClick={handleCancel}
          data-testid="create-project-preview-cancel"
        >
          Cancel
        </Button>
      </DrawerFooter>
    </>
  );
};

CreateProjectPreview.displayName = 'CreateProjectPreview';

CreateProjectPreview.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string.isRequired,
    quantity: PropTypes.number,
    sku: PropTypes.string,
  })),
  singleProduct: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    sku: PropTypes.string,
  })),
  multipleProduct: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    sku: PropTypes.string,
  })),
  onProjectCreatedAndItemAdded: PropTypes.func,
  copyLocation: PropTypes.string,
};

CreateProjectPreview.defaultProps = {
  products: [],
  singleProduct: [],
  multipleProduct: [],
  onProjectCreatedAndItemAdded: () => { },
  copyLocation: '',
};

CreateProjectPreview.dataModel = extend(
  ProductImage,
  InlineProjectCreation,
);
